import { Component, OnInit } from '@angular/core';
import { Pagador } from 'src/app/dashboard/interfaces/pagador';
import { PagadorService } from 'src/app/dashboard/services/pagador.service';

interface EstadoFiltro {
  id: number | null;
  label: string;
}

/**
 * Vista del OPERADOR: lista global de pagadores por compliance, con filtro por
 * estado (Por aprobar / Aprobados / Rechazados / Blacklist / Todos) y contador
 * de pendientes. Reutiliza `app-list-payer` en modo admin (`setPagadores`).
 */
@Component({
  selector: 'app-payer-compliance-list',
  templateUrl: './payer-compliance-list.component.html',
  styleUrls: ['./payer-compliance-list.component.sass'],
})
export class PayerComplianceListComponent implements OnInit {
  private todos: Pagador[] = [];
  pagadores: Pagador[] = [];
  loading = false;

  /** POR_APROBAR = estadoComplianceID 0 (catálogo de compliance). */
  readonly ESTADO_POR_APROBAR = 0;

  /** Estado seleccionado (null = Todos). Por defecto, "Por aprobar". */
  estadoSeleccionado: number | null = this.ESTADO_POR_APROBAR;

  /** Orden y etiquetas por defecto del catálogo (fallback si falta descripción). */
  private readonly ORDEN = [0, 3, 1, 2];
  private readonly LABELS: Record<number, string> = {
    0: 'Por aprobar',
    3: 'Aprobados',
    1: 'Rechazados',
    2: 'Blacklist',
  };

  constructor(private service: PagadorService) {}

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      const data = await this.service.getPagadoresCompliance();
      // Del ultimo al primero: mas recientes arriba (por fecha de registro).
      this.todos = [...data].sort((a, b) => this.fechaOrden(b) - this.fechaOrden(a));
    } catch {
      this.todos = [];
    } finally {
      this.loading = false;
      this.applyFilter();
    }
  }

  /** Opciones de filtro: Todos + estados del catálogo + cualquier estado extra en la data. */
  get estados(): EstadoFiltro[] {
    const items: EstadoFiltro[] = [{ id: null, label: 'Todos' }];
    const vistos = new Set<number>();
    for (const id of this.ORDEN) {
      vistos.add(id);
      items.push({ id, label: this.labelDe(id) });
    }
    for (const p of this.todos) {
      if (!vistos.has(p.estadoComplianceID)) {
        vistos.add(p.estadoComplianceID);
        items.push({ id: p.estadoComplianceID, label: this.labelDe(p.estadoComplianceID) });
      }
    }
    return items;
  }

  /** Etiqueta desde la data (estadoCompliance.descripcion) o fallback del catálogo. */
  private labelDe(id: number): string {
    const desc = this.todos.find((p) => p.estadoComplianceID === id)?.estadoCompliance
      ?.descripcion;
    return desc || this.LABELS[id] || `Estado ${id}`;
  }

  /** Timestamp para ordenar; usa created_at y cae a 0 si falta o es invalido. */
  private fechaOrden(p: Pagador): number {
    const t = new Date(p?.created_at ?? 0).getTime();
    return isNaN(t) ? 0 : t;
  }

  count(id: number | null): number {
    if (id === null) return this.todos.length;
    return this.todos.filter((p) => p.estadoComplianceID === id).length;
  }

  get pendientesCount(): number {
    return this.count(this.ESTADO_POR_APROBAR);
  }

  seleccionar(id: number | null): void {
    this.estadoSeleccionado = id;
    this.applyFilter();
  }

  private applyFilter(): void {
    // Nueva referencia para que el setter `setPagadores` de app-list-payer corra.
    this.pagadores =
      this.estadoSeleccionado === null
        ? [...this.todos]
        : this.todos.filter((p) => p.estadoComplianceID === this.estadoSeleccionado);
  }
}
